import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { Group } from '../../../groups/entities/Group.js';
import type { Person } from '../../../users/entities/person.js';
import { MicrosoftAPI } from '../../api/MicrosoftAPI.js';
import { isAccountEnabledInEntra } from '../../api/microsoftAccountLifecycle.js';

/**
 * Shown when a person has no Entra account yet.
 *
 * Phrased as something to do rather than as a failure: for a newly created person this is simply the
 * state before the sync has run, and the account is created moments later on its own. The button is
 * there for the case where it did not - unless the person's state wants no account, then it says so instead.
 */
export const MicrosoftUserNotInEntra = stateful(
  (props: {
    user: NarrowedEntity<Person, { _id: 1; roles: { name: 1; contexts: 1 }; microsoftUserData: { microsoftID: 1 } }>;
  }) =>
    !isAccountEnabledInEntra(props.user)
      ? html`
          <div class="card-container">
            <div class="card card-body items-center gap-4 p-8 text-center">
              <span class="icon-[material-symbols--cloud-off] text-5xl text-base-content/50"></span>
              <div class="flex flex-col gap-1">
                <h2 class="font-semibold text-base-content md:text-lg">Für diese Person ist kein Konto vorgesehen</h2>
                <p class="max-w-prose text-sm text-base-content/70">
                  Ein Konto in Entra haben Personen in Bewerbung oder aktiv als Mitarbeitende, Schüler, Sorgeberechtigte
                  oder Spender, und Personen mit einer internen Rolle. Ehemalige behalten ihr Konto deaktiviert, neu
                  angelegt wird es nicht.
                </p>
              </div>
            </div>
          </div>
        `
      : html`
          <div class="card-container">
            <div class="card card-body items-center gap-4 border-warning/40 bg-warning/5 p-8 text-center">
              <span class="icon-[material-symbols--cloud-off] text-5xl text-warning"></span>
              <div class="flex flex-col gap-1">
                <h2 class="font-semibold text-base-content md:text-lg">Dieser Nutzer hat noch kein Konto in Entra</h2>
                <p class="max-w-prose text-sm text-base-content/70">
                  Neue Nutzer erhalten ihr Konto automatisch kurz nach dem Anlegen. Bleibt diese Meldung bestehen, kann
                  das Konto hier von Hand angelegt werden.
                </p>
              </div>
              <button
                class="btn btn-primary"
                @click=${() => guardDialog(() => MicrosoftAPI.repairUser(props.user._id)())}
              >
                <span class="icon-[material-symbols--build]"></span>
                Konto jetzt anlegen
              </button>
            </div>
          </div>
        `,
);

/** Shown when a group has no counterpart in Entra yet. @see MicrosoftUserNotInEntra */
export const MicrosoftGroupNotInEntra = stateful(
  (props: { group: NarrowedEntity<Group, { _id: 1; microsoftGroupData: { microsoftID: 1 } }> }) => html`
    <div class="card-container">
      <div class="card card-body items-center gap-4 p-8 text-center">
        <span class="icon-[material-symbols--group-off] text-5xl text-info"></span>
        <div class="flex flex-col gap-1">
          <h2 class="font-semibold text-base-content md:text-lg">Diese Gruppe ist nicht in Entra hinterlegt</h2>
          <p class="max-w-prose text-sm text-base-content/70">
            Nicht jede Gruppe braucht eine Entsprechung in Entra. Wird sie dort gebraucht, kann sie hier angelegt
            werden.
          </p>
        </div>
        <button class="btn btn-primary" @click=${() => guardDialog(() => MicrosoftAPI.repairGroup(props.group._id)())}>
          <span class="icon-[material-symbols--build]"></span>
          Gruppe in Entra anlegen
        </button>
      </div>
    </div>
  `,
);
